import Papa from "papaparse";

import ApiService from "../../services/ApiService";
import { formatDatePickerValue } from "../../services/TimeService";
import translate from "../../services/Translate";
import { detectCsvDelimiter } from "../../services/UtilityService";
import { DiplomaImportFile, DiplomaImportStructuredData } from "./DiplomaImport.d";

export const createImportedDiplomas = (
  data: Array<DiplomaImportStructuredData>,
  language: string,
  setSubmitting: Function,
  showAlert: Function,
  popAlert: Function
) => {
  setSubmitting(true);

  showAlert?.({
    type: "info",
    title: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.LOADING.TITLE"),
    message: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.LOADING.MESSAGE"),
    clickOutsideToClose: false,
    buttons: [],
  });

  ApiService.post("diplomas/batch", {
    users: data
      .filter((user) => user.diplomable)
      .map(({ rowIndex, diplomable, ...user }) => ({
        ...user,
        birthdate: formatDatePickerValue(user.birthdate),
      })),
  })
    .then((response) => {
      popAlert?.();

      if (response.data.success) {
        const { skipped_diplomas, succeeded_diplomas, total_diplomas } = response.data.diplomas;

        showAlert?.({
          type: "success",
          title: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.SUCCESS.TITLE"),
          message: `${succeeded_diplomas} / ${total_diplomas} ${translate(
            language,
            "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.SUCCESS.MESSAGE"
          )}`,
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "GLOBAL.OK"),
              onClick: () => popAlert?.(),
            },
          ],
          extraContent:
            skipped_diplomas.length > 0 ? (
              <div className="text-start white-background p-2 rounded border">
                <strong>
                  {skipped_diplomas.length} {translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.SKIPPED")}
                </strong>
                <ul>
                  {skipped_diplomas.map((skipped: DiplomaImportStructuredData, index: number) => (
                    <li key={`skipped-${index}`}>
                      {skipped.email} - {skipped.cursus}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null,
        });
      } else {
        showAlert?.({
          type: "danger",
          title: translate(language, "ERROR.API_ERROR"),
          message: response.data.error || translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.ERROR.GENERIC"),
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "GLOBAL.CLOSE"),
              variant: "danger",
              onClick: () => popAlert?.(),
            },
          ],
        });
      }
    })
    .catch((err) => {
      popAlert?.();

      showAlert?.({
        type: "danger",
        title: translate(language, "ERROR.API_ERROR"),
        message: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.ERROR.GENERIC"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: err?.data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(err.data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => setSubmitting(false));
};

export const validateImportedDiplomas = (
  data: Array<DiplomaImportStructuredData>,
  language: string,
  setValidatedData: Function,
  setValidating: Function,
  showAlert: Function,
  popAlert: Function
) => {
  setValidating(true);

  ApiService.post("diplomas/batch/check", {
    users: data.map((user) => {
      const { rowIndex, diplomable, ...userWithoutExtraFields } = user;
      return userWithoutExtraFields;
    }),
  })
    .then((response) => {
      popAlert?.();
      if (response.data && response.data.users) {
        setValidatedData(
          response.data.users.map((user: any, index: number) => ({
            ...user,
            rowIndex: index,
          }))
        );
      }
    })
    .catch((err) => {
      popAlert?.();

      showAlert?.({
        type: "danger",
        title: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.VALIDATION.ERROR.TITLE"),
        message: translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.VALIDATION.ERROR.MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: err?.data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(err.data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => setValidating(false));
};

export const importCsvFile = (
  file: File,
  language: string,
  setCsvData: Function,
  setValidatedData: Function,
  setValidating: Function,
  showAlert: Function,
  popAlert: Function
) => {
  detectCsvDelimiter(file).then((delimiter) => {
    Papa.parse<DiplomaImportFile>(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: delimiter,
      complete: (results: Papa.ParseResult<DiplomaImportFile>) => {
        if (results.errors.length > 0) {
          console.error("CSV Parse Errors:", results.errors);
          alert(translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.ERROR.CSV_PARSE"));
          return;
        }
        const transformedData = results.data.map((row: DiplomaImportFile, index: number) => {
          const baseData = {
            cursus: row.Cursus,
            name: row.Name,
            birthdate: row.Birthdate,
            date: row?.Date?.trim() || new Date().toLocaleDateString("FR"),
            email: row.Email,
            is_mines: row["Is Mines"] === "1" && row.ECE !== "1",
            in_english: row["Mail in English"] === "1",
            is_ece: row.ECE === "1" && row["Is Mines"] !== "1",
            is_b2b: row.B2B === "1",
            send_email: row["Send Email"] === undefined || row["Send Email"] === "1",
            is_public: row["Is Public"] === undefined || row["Is Public"] === "1",
            rowIndex: index,
          };

          return baseData;
        });

        setCsvData(transformedData);
        validateImportedDiplomas(transformedData, language, setValidatedData, setValidating, showAlert, popAlert);
      },
      error: (error: Error) => {
        console.error("Error parsing CSV:", error);
        alert(`${translate(language, "PAGES.DIPLOMA_IMPORT.ALERT.IMPORT.ERROR.CSV_PARSE")} ${error.message}`);
      },
    });
  });
};

export const processFileInput = (
  event: React.ChangeEvent<HTMLInputElement>,
  importCsvFileCallback: (file: File) => void
) => {
  const file = event.target.files?.[0];
  if (file) {
    importCsvFileCallback(file);
  }
};

export const updateField = <T,>(
  rowIndex: number,
  field: string,
  value: T,
  setCsvData: Function,
  setValidatedData: Function,
  additionalCallback?: Function
) => {
  const updatedData = (prevData: any[]) =>
    prevData.map((item, index) => (index === rowIndex ? { ...item, [field]: value } : item));

  setCsvData(updatedData);
  setValidatedData(updatedData);

  additionalCallback?.();
};
